// Inspiracion.jsx
"use client";

import { useState, useEffect, useCallback } from "react";
// import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { supabase } from "../../../Supabase/SupabaseClient";
import { getPublicUrl } from "../../../config/supabaseImages";
import ImageCarousel from "../Inspiracion/image-carousel/ImageCarousel";
import './Inspiracion.css';
import Footer from "../../../Components/Footer/Footer";

// Custom hook para la lógica del carrusel
export const useCarousel = (totalItems, autoplayDelay = 5000) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const goToNext = useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex + 1) % totalItems);
  }, [totalItems]);
  const goToPrev = useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex - 1 + totalItems) % totalItems);
  }, [totalItems]);
  const goToIndex = useCallback(index => setCurrentIndex(index), []);

  useEffect(() => {
    if (totalItems <= 1) return;
    const interval = setInterval(goToNext, autoplayDelay);
    return () => clearInterval(interval);
  }, [goToNext, totalItems, autoplayDelay]);

  return { currentIndex, goToNext, goToPrev, goToIndex };
};

// Hook personalizado para gestionar favoritos con UUID real
const useFavoritos = usuario_id => {
  const [favoritos, setFavoritos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarFavoritos = async () => {
      if (!usuario_id) return;
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('favoritos_usuario')
          .select('imagen_id')
          .eq('usuario_id', usuario_id);
        if (error) throw error;
        setFavoritos(data.map(fav => fav.imagen_id));
      } catch (error) {
        console.error("Error al cargar favoritos:", error);
      } finally {
        setLoading(false);
      }
    };
    cargarFavoritos();
  }, [usuario_id]);

  const toggleFavorito = async imagenId => {
    if (!usuario_id) {
      console.log("Debes iniciar sesión para guardar favoritos");
      return false;
    }
    try {
      const esFav = favoritos.includes(imagenId);
      if (esFav) {
        const { error } = await supabase
          .from('favoritos_usuario')
          .delete()
          .eq('usuario_id', usuario_id)
          .eq('imagen_id', imagenId);
        if (error) throw error;
        setFavoritos(favs => favs.filter(id => id !== imagenId));
      } else {
        const { error } = await supabase
          .from('favoritos_usuario')
          .insert({ usuario_id: usuario_id, imagen_id: imagenId });
        if (error) throw error;
        setFavoritos(favs => [...favs, imagenId]);
      }
      return true;
    } catch (error) {
      console.error("Error al gestionar favorito:", error);
      return false;
    }
  };

  const esFavorito = imagenId => favoritos.includes(imagenId);
  return { favoritos, loading, toggleFavorito, esFavorito };
};

export default function Inspiracion() {
  const [estilosFotograficos, setEstilosFotograficos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  // Obtener el UUID del usuario autenticado
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
  }, []);

  const { toggleFavorito, esFavorito } = useFavoritos(userId);

  const handleToggleFavorito = async imagenId => {
    const ok = await toggleFavorito(imagenId);
    if (!ok) {
      // aquí puedes mostrar un mensaje o redirigir a login
    }
  };

  useEffect(() => {
    async function cargarEstilos() {
      try {
        setLoading(true);
        const { data: estilosData, error: estilosError } = await supabase
          .from('estilos_fotograficos')
          .select('*')
          .order('id');
        if (estilosError) throw estilosError;

        const estilosConImagenes = await Promise.all(
          estilosData.map(async estilo => {
            const { data: imagenesData, error: imagenesError } = await supabase
              .from('imagenes_inspiracion')
              .select('*')
              .eq('estilo_id', estilo.id)
              .order('orden');
            if (imagenesError) throw imagenesError;

            const imagenesConUrl = imagenesData.map(imagen => {
              // Usar la función getPublicUrl de la configuración
              const publicUrl = getPublicUrl('inspiracion', imagen.ruta_imagen);
              return {
                id: imagen.id,
                url: publicUrl,
                titulo: imagen.titulo || "Sin título",
                creditos: imagen.creditos || "Sin créditos"
              };
            });
            return { ...estilo, imagenes: imagenesConUrl };
          })
        );
        setEstilosFotograficos(estilosConImagenes);
      } catch (error) {
        console.error("Error al cargar estilos fotográficos:", error);
        setError("No se pudieron cargar los estilos fotográficos");
      } finally {
        setLoading(false);
      }
    }
    cargarEstilos();
  }, []);

  if (loading) return (
    <div className="insp-loading" aria-live="polite">
      <div className="insp-spinner"></div>
      <p>Cargando estilos fotográficos...</p>
    </div>
  );

  if (error) return (
    <div className="insp-error" role="alert">
      <p>{error}</p>
      <button onClick={() => window.location.reload()} className="insp-btn insp-btn--retry">
        Intentar nuevamente
      </button>
    </div>
  );

  return (
    <>
    <div className="insp-container">
      <h1 className="insp-main-title">Inspiración Fotográfica</h1>
      <p className="insp-main-desc">Explora diferentes estilos fotográficos para inspirar tu próxima sesión</p>

     

      {estilosFotograficos.length === 0 ? (
          <div className="insp-empty">No hay estilos fotográficos disponibles en este momento.</div>
        ) : (
          <div className="insp-grid">
            {estilosFotograficos.map((estilo, index) => (
              <section key={estilo.id} className="insp-section">
                <h2 className="insp-section-title">{estilo.nombre}</h2>
                <p className="insp-section-desc">{estilo.descripcion}</p>
                <ImageCarousel
                  images={estilo.imagenes}
                  autoplayDelay={5000 + index * 1000}
                  onToggleFavorito={handleToggleFavorito}
                  esFavorito={esFavorito}
                />
              </section>
            ))}
          </div>
        )
      }
    </div>
      <Footer />
    </>
  );
}